/**
 * Post-confirmation consent handling
 * Registers GDPR consents after email verification
 * 
 * This is necessary because:
 * 1. During registration with email confirmation, no session exists
 * 2. RLS-protected tables cannot be written to without a session
 * 3. Consents are stored in user_metadata during signUp
 * 4. After email confirmation, we read metadata and persist consents
 */

import { supabase } from '@/lib/supabase';
import { getClientIP } from '@/utils/clientIP';
import { debugLog, debugError } from '@/utils/debugLogger';
import { PRIVACY_POLICY_VERSION } from '@/services/consent/userConsentService';

/**
 * Save consents to database after email confirmation
 * Called from verify.tsx when user confirms their email
 * 
 * @param userId - The authenticated user's ID
 * @param privacyAccepted - Privacy policy consent from user_metadata
 * @param marketingConsent - Marketing consent from user_metadata
 */
export async function savePostConfirmationConsents(
  userId: string,
  privacyAccepted: boolean,
  marketingConsent: boolean
): Promise<boolean> {
  try {
    debugLog('auth', 'Saving post-confirmation consents for user:', userId, 'PostConfirmationConsent');
    
    const ipAddress = await getClientIP();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const now = new Date().toISOString();
    
    // 1. Upsert user_preferences with consent data
    const { error: prefsError } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: userId,
        privacy_accepted: privacyAccepted,
        privacy_accepted_at: privacyAccepted ? now : null,
        privacy_policy_version: PRIVACY_POLICY_VERSION,
        marketing_consent: marketingConsent,
        marketing_consent_at: marketingConsent ? now : null,
      }, { onConflict: 'user_id' });
    
    if (prefsError) {
      debugError('auth', 'Failed to upsert user_preferences:', prefsError, 'PostConfirmationConsent');
      // Continue anyway - consent_logs are more important for GDPR
    } else {
      debugLog('auth', 'user_preferences upserted successfully', undefined, 'PostConfirmationConsent');
    }
    
    // 2. Log PRIVACY_POLICY consent (Art. 7 GDPR)
    const { error: privacyLogError } = await supabase
      .from('consent_logs')
      .insert({
        user_id: userId,
        consent_type: 'PRIVACY_POLICY',
        granted: privacyAccepted,
        source: 'registration_email_confirmed',
        ip_address: ipAddress,
        user_agent: userAgent,
      });
    
    if (privacyLogError) {
      debugError('auth', 'Failed to log PRIVACY_POLICY consent:', privacyLogError, 'PostConfirmationConsent');
    } else {
      debugLog('auth', 'PRIVACY_POLICY consent logged', undefined, 'PostConfirmationConsent');
    }
    
    // 3. Log MARKETING consent (Art. 7 GDPR - accountability requires logging even if false)
    const { error: marketingLogError } = await supabase
      .from('consent_logs')
      .insert({
        user_id: userId,
        consent_type: 'MARKETING',
        granted: marketingConsent,
        source: 'registration_email_confirmed',
        ip_address: ipAddress,
        user_agent: userAgent,
      });
    
    if (marketingLogError) {
      debugError('auth', 'Failed to log MARKETING consent:', marketingLogError, 'PostConfirmationConsent');
    } else {
      debugLog('auth', `MARKETING consent logged: ${marketingConsent}`, undefined, 'PostConfirmationConsent');
    }
    
    // Consider success if at least one consent was logged
    const success = !privacyLogError || !marketingLogError;
    
    if (success) {
      debugLog('auth', 'Post-confirmation consents saved successfully', undefined, 'PostConfirmationConsent');
    }
    
    return success;
  } catch (error) {
    debugError('auth', 'Exception saving post-confirmation consents:', error, 'PostConfirmationConsent');
    return false;
  }
}
